import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Search, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const stageLabels = {
    new_lead: "Ny lead", contacted: "Kontaktad", booked_test_drive: "Bokad provkörning",
    warm_lead: "Varm lead", offer_sent: "Offert skickad", won: "Vunnen", lost: "Förlorad",
};
const stageColors = {
    new_lead: "bg-blue-100 text-blue-800", contacted: "bg-purple-100 text-purple-800",
    booked_test_drive: "bg-orange-100 text-orange-800", warm_lead: "bg-yellow-100 text-yellow-800",
    offer_sent: "bg-indigo-100 text-indigo-800", won: "bg-green-100 text-green-800", lost: "bg-red-100 text-red-800",
};

export default function AdminContacts() {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", source: "manual" });

    const fetchContacts = (q = "") => {
        const params = q ? `?search=${q}` : "";
        api.get(`/contacts${params}`).then((r) => setContacts(r.data)).catch(console.error).finally(() => setLoading(false));
    };

    useEffect(() => { fetchContacts(); }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchContacts(search);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post("/contacts", form);
            toast.success("Kontakt skapad");
            setShowCreate(false);
            setForm({ name: "", email: "", phone: "", source: "manual" });
            fetchContacts();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Kunde inte skapa kontakt");
        }
    };

    return (
        <div className="p-8" data-testid="admin-contacts">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-medium tracking-tight">Kontakter</h1>
                <Button data-testid="create-contact-btn" onClick={() => setShowCreate(true)} className="bg-black text-white hover:bg-neutral-800">
                    <Plus size={16} className="mr-2" /> Ny kontakt
                </Button>
            </div>

            <form onSubmit={handleSearch} className="flex gap-2 mb-4">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input data-testid="contact-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Sök kontakter..." className="pl-9" />
                </div>
                <Button type="submit" variant="outline">Sök</Button>
            </form>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs uppercase tracking-wider">Namn</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider">E-post</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider">Telefon</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider">Steg</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider">Källa</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-right">Visa</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {contacts.map((c) => (
                            <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                                <TableCell className="text-sm font-medium">{c.name}</TableCell>
                                <TableCell className="text-sm text-neutral-500">{c.email}</TableCell>
                                <TableCell className="text-sm text-neutral-500">{c.phone}</TableCell>
                                <TableCell>
                                    <Badge className={`text-[10px] ${stageColors[c.pipeline_stage] || "bg-neutral-100"}`}>
                                        {stageLabels[c.pipeline_stage] || c.pipeline_stage}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-neutral-400 capitalize">{c.source}</TableCell>
                                <TableCell className="text-right">
                                    <Link to={`/admin/contacts/${c.id}`}>
                                        <Button variant="ghost" size="sm" data-testid={`view-contact-${c.id}`}><Eye size={16} /></Button>
                                    </Link>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!contacts.length && !loading && (
                            <TableRow><TableCell colSpan={6} className="text-center text-neutral-400 py-8">Inga kontakter</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Create Contact Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent data-testid="create-contact-dialog">
                    <DialogHeader><DialogTitle>Ny kontakt</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div><Label className="text-xs uppercase tracking-wider">Namn</Label><Input data-testid="new-contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" /></div>
                        <div><Label className="text-xs uppercase tracking-wider">E-post</Label><Input data-testid="new-contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" /></div>
                        <div><Label className="text-xs uppercase tracking-wider">Telefon</Label><Input data-testid="new-contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
                        <Button data-testid="submit-contact-btn" type="submit" className="w-full bg-black text-white">Skapa kontakt</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
